/**
 * Source Articles Page
 *
 * Displays articles from a specific domain/source.
 * Linked from the Sources page — a focused, lightweight alternative
 * to the full Dashboard.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLoading from '../components/PageLoading';
import EmptyState from '../components/EmptyState';
import { getArticles } from '../services/articles';
import ArticleCard from '../components/ArticleCard';
import Pagination from '../components/Pagination';
import { setTitle } from '../utils/environment';

const PER_PAGE = 20;

const SourceArticles = () => {
  const { domain } = useParams();
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const decodedDomain = decodeURIComponent(domain);

  const getDisplayName = (d) => {
    if (!d || d === 'pending' || d === 'unknown') return 'Unknown Source';
    if (d.startsWith('wechat/')) {
      const author = d.slice('wechat/'.length);
      if (author && author !== 'unknown') {
        return `WeChat: ${author.charAt(0).toUpperCase() + author.slice(1)}`;
      }
      return 'WeChat: Unknown Author';
    }
    if (d === 'mp.weixin.qq.com') return 'WeChat Articles';
    return d;
  };

  const fetchArticles = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const data = await getArticles({ page, per_page: PER_PAGE, domain: decodedDomain });
      setArticles(data.articles || []);
      setPagination({
        total: data.total || 0,
        page: data.page || page,
        total_pages: data.total_pages || 1,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [decodedDomain]);

  useEffect(() => {
    setTitle(`${getDisplayName(decodedDomain)} · Read It Never`);
    fetchArticles(1);
  }, [fetchArticles, decodedDomain]);

  const handlePageChange = (page) => {
    fetchArticles(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleDelete = (articleId) => {
    setArticles((prev) => prev.filter((a) => a.id !== articleId));
    setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  };

  if (isLoading && articles.length === 0) {
    return (
      <PageLoading message="Loading articles..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8">
          <div className="text-fun-coral mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-heading mb-2">Failed to Load Articles</h2>
          <p className="text-secondary-text mb-4">{error}</p>
          <button onClick={() => fetchArticles(1)} className="btn-clay-accent">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface shadow-clay border-b-3 border-clay-border">
        <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <Link
              to="/sources"
              className="mr-4 p-2 text-tertiary-text hover:text-primary-600 hover:bg-primary-50 rounded-clay transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-heading">
                {getDisplayName(decodedDomain)}
              </h1>
              <p className="text-sm text-secondary-text mt-1">
                {pagination.total} {pagination.total === 1 ? 'article' : 'articles'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Article List */}
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {articles.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-tertiary-text mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            }
            title="No Articles"
            description="No articles found from this source."
          />
        ) : (
          <>
            <div className="space-y-4">
              {articles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onDelete={handleArticleDelete}
                />
              ))}
            </div>

            {pagination.total_pages > 1 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total}
                itemsPerPage={PER_PAGE}
                onPageChange={handlePageChange}
                isLoading={isLoading}
                className="mt-8"
                itemLabel="articles"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SourceArticles;
